/**
 * Record-type URL paths — the ONE place the organizer-side `auditions` vs
 * `competitions` route segment is decided (BAL: separate competitions nav).
 *
 * Competitions are `auditions` rows with `is_competition = true` (single
 * entity, single editor — Critical Rule #8), but they get their OWN route
 * trees: organizer detail at `/home/[account]/competitions/[id]/…` and the
 * public page at `/competitions/[slug]`. Auditions keep `/…/auditions/…`.
 *
 * Every callsite that builds such a URL MUST go through these helpers rather
 * than hand-writing the segment, so the fork lives in one place (mirrors the
 * wording single-source in `record-terminology.ts`). Pairs with `recordTypeOf`
 * there: `recordManageRoot(accountSlug, recordTypeOf(audition), audition.slug ?? audition.id)`.
 *
 * The record identifier segment is the audition's SLUG in canonical URLs
 * (human-readable, stable), with the UUID as a fallback for slugless legacy
 * rows. Callers pass `audition.slug ?? audition.id`; UUID URLs still resolve
 * (the management layout 308-redirects them to the slug URL).
 */
import type { RecordType } from './record-terminology';

/** Organizer-side URL segment for a record type. */
export function recordRouteSegment(
  type: RecordType,
): 'auditions' | 'competitions' {
  return type === 'competition' ? 'competitions' : 'auditions';
}

/**
 * Organizer-side record detail root, e.g. `/home/acme/competitions/<slug>`.
 * Append sub-paths (`/details`, `/applications?view=list`) at the callsite.
 * Pass `audition.slug ?? audition.id` as `slugOrId` — the slug is canonical,
 * the UUID is the fallback for slugless legacy rows.
 */
export function recordManageRoot(
  accountSlug: string,
  type: RecordType,
  slugOrId: string,
): string {
  return `/home/${accountSlug}/${recordRouteSegment(type)}/${slugOrId}`;
}

/** Organizer-side list root, e.g. `/home/acme/competitions`. */
export function recordManageListRoot(
  accountSlug: string,
  type: RecordType,
): string {
  return `/home/${accountSlug}/${recordRouteSegment(type)}`;
}

/**
 * Public (dancer-facing) record page href, e.g. `/competitions/<slug>`.
 * Built from the `is_competition` boolean directly (the public surfaces have
 * the flag, not always a resolved RecordType). Returned as a plain string so
 * the route-link validator doesn't false-positive on a ternary inside JSX.
 */
export function publicRecordHref(isCompetition: boolean, slug: string): string {
  const segment = isCompetition ? 'competitions' : 'auditions';
  return `/${segment}/${slug}`;
}

/** Both organizer-side route patterns (auditions + competitions). */
export const RECORD_MANAGE_ROUTE_SEGMENTS = [
  'auditions',
  'competitions',
] as const;

/**
 * Both organizer-side route patterns for `revalidatePath` after a record
 * mutation. Because a record's tree depends on `is_competition` (and some
 * server actions don't fetch it), the simplest correct invalidation is to
 * revalidate BOTH patterns — over-revalidating one unaffected route pattern is
 * cheap and avoids threading the flag into every action.
 *
 * @example recordManageRevalidatePatterns(`/${auditionId}`)
 *   → ['/home/[account]/auditions/<id>', '/home/[account]/competitions/<id>']
 */
export function recordManageRevalidatePatterns(suffix = ''): string[] {
  return RECORD_MANAGE_ROUTE_SEGMENTS.map(
    (segment) => `/home/[account]/${segment}${suffix}`,
  );
}
